import { gql } from "@apollo/client";

export const PROJECTS = gql`
  query Projects(
    $where: ProjectWhereInput
    $like: JSONObject
    $isIn: JSONObject
    $take: Int
    $skip: Int
  ) {
    projects(
      where: $where
      like: $like
      isIn: $isIn
      take: $take
      skip: $skip
    ) {
      data {
        id
        name
        createdDate
        locked
        ria
        visible
        tracer {
          id
          name
        }
        contract {
          issuanceDate
        }
        statuses {
          id
          createdDate
          status {
            id
            template
            name
            tag
            progress
            order
            color
            background
            createdDate
            actions {
              id
              type
              args
              conditions {
                id
                operation
                rightOperand
                leftOperand
              }
            }
            process {
              id
              type
              processStatuses {
                id
                name
                tag
                template
                progress
                order
              }
            }
          }
        }
        fields
        contact {
          id
          relatedPersons {
            id
            relationship
            authority
            identity {
              id
              socialRegime
              socialSecurityNumber
              fields
              user {
                id
                firstname
                lastname
                birthDate
                gender
                birthName
              }
            }
          }

          user {
            firstname
            lastname
            gender
          }
        }
        hasResponsible
        author {
          id
          firstname
          lastname
        }
        subscriptions {
          id
          status
        }
        expired
      }
      count
    }
  }
`;

export const PROJECTS_LIST = gql`
  query ProjectsList(
    $where: ProjectWhereInput
    $exclude: [String]
    $withSubscriptions: Boolean!
    $withContract: Boolean!
    $withProcess: Boolean!
    $withTracers: Boolean!
    $withFlows: Boolean!
    $withProcessStatuses: Boolean!
    $like: JSONObject
    $isIn: JSONObject
    $take: Int
    $skip: Int
    $scope: ProjectsScope
    $advancedFilters: [AdvancedFilterInput]
  ) {
    projectsList(
      where: $where
      exclude: $exclude
      like: $like
      isIn: $isIn
      take: $take
      skip: $skip
      scope: $scope
      advancedFilters: $advancedFilters
    ) {
      data {
        id
        name
        createdDate
        locked
        visible
        ria
        tracer {
          id
          name
          flow {
            process {
              config {
                type
                value
              }
            }
          }
        }
        contract @include(if: $withContract) {
          issuanceDate
        }
        statuses {
          id
          createdDate
          status {
            id
            template
            name
            tag
            progress
            order
            color
            background
            createdDate
            process @include(if: $withProcess) {
              id
              type
              flows @include(if: $withFlows) {
                id
                tracers @include(if: $withTracers) {
                  id
                }
              }
              processStatuses @include(if: $withProcessStatuses) {
                id
                name
                tag
                template
                progress
                order
                possibleStatuses @include(if: false) {
                  id
                  nextStatus
                  conditions @include(if: false) {
                    id
                    operation
                    rightOperand
                    leftOperand
                  }
                }
                actions @include(if: false) {
                  id
                  type
                  args
                  conditions @include(if: false) {
                    id
                    operation
                    rightOperand
                    leftOperand
                  }
                }
              }
            }
          }
        }
        fields
        contact {
          id
          telecoms {
            id
            value
            system
          }
          user {
            firstname
            lastname
            gender
          }
          fields
        }
        hasResponsible
        author {
          id
          firstname
          lastname
          organization {
            id
          }
          poles {
            id
          }
        }
        assignedUsers {
          id
        }
        subscriptions @include(if: $withSubscriptions) {
          id
          status
        }
        expired
        traceability
      }
      count
    }
  }
`;

export const COUNT_PROJECTS = gql`
  query CountProjects(
    $where: ProjectWhereInput
    $like: JSONObject
    $isIn: JSONObject
    $scope: ProjectsScope
    $advancedFilters: [AdvancedFilterInput]
  ) {
    countProjects(
      where: $where
      like: $like
      isIn: $isIn
      scope: $scope
      advancedFilters: $advancedFilters
    )
  }
`;

export const PROJECT = gql`
  query Project($where: ProjectWhereInput) {
    project(where: $where) {
      id
      visible
      externalId
      name
      ria
      locked
      projectType
      tracer {
        id
        name
        type
        flow {
          id
          process {
            id
            theme
            type
            config {
              type
              value
            }
          }
        }
      }
      campaign {
        id
      }
      terminationRequest {
        id
        contractNumber
        reason
        recipient
        dueDate
        madeIn
        address
        complement
        zipCode
        city
        email
        brokerageMandate {
          id
          firstName
          lastName
          currentCompany
        }
      }
      statuses {
        id
        createdDate
        status {
          id
          template
          name
          tag
          progress
          order
          color
          background
          moveToNext
          createdDate
          formDefinition
          possibleStatuses {
            order
            nextStatus
            conditions {
              operation
              rightOperand
              leftOperand
            }
          }
          actions {
            id
            type
            args
            conditions {
              id
              operation
              rightOperand
              leftOperand
            }
          }
          screens {
            id
            currentPublishedVersion {
              id
              versionNumber
              status
              htmlContent
              styleContent
              bindingManifest {
                id
                slots
              }
              translationTable {
                id
                entries
              }
              states {
                id
                name
                order
                htmlContent
                conditions
                bindingManifest {
                  id
                  slots
                }
              }
            }
          }
          process {
            id
            theme
            type
            processStatuses {
              id
              name
              tag
              template
              progress
              order
              color
              background
              moveToNext
              possibleStatuses {
                id
                nextStatus
                conditions {
                  id
                  operation
                  rightOperand
                  leftOperand
                }
              }
              actions {
                id
                type
                args
                conditions {
                  id
                  operation
                  rightOperand
                  leftOperand
                }
              }
            }
          }
        }
      }
      fields
      contact {
        id
        profession
        socialRegime
        socioProfessionalCategory
        socialSecurityNumber
        exerciseFrame
        nationality
        politicallyExposedPerson
        acceptContact
        acceptContactDate
        acceptDigital
        optMadelin
        familySituation
        telecoms {
          id
          system
          value
        }
        user {
          id
          firstname
          lastname
          gender
          phone
          birthDate
        }
        relatedPersons {
          id
          relationship
          authority
          identity {
            id
            socialRegime
            socioProfessionalCategory
            socialSecurityNumber
            fields
            user {
              id
              gender
              firstname
              lastname
              birthDate
            }
          }
        }
        telecoms {
          id
          system
          value
        }
        fields
      }
      contract {
        id
        identifier
        issuanceDate
        saleMode
      }
      locked
      retroactive
      expired
      subscriptions {
        id
        status
        insurancePlan {
          id
          config
        }
        attachments {
          id
          name
          filename
          fileUrl
          contentType
          status
          type
        }
      }
      author {
        id
        firstname
        lastname
        email
        phone
        address
        zipCode
        city
        organization {
          id
          name
          broker {
            socialReason
            orias
            status
          }
          telecoms {
            id
            system
            value
          }
        }
      }
    }
  }
`;

export const PROJECT_BROKER_TRACER_ID = gql`
  query ProjectBrokerTracerId($where: ProjectWhereInput) {
    project(where: $where) {
      id
      tracer {
        id
      }
      statuses {
        id
        status {
          id
          process {
            id
          }
        }
      }
    }
  }
`;

export const PROJECT_ELIGIBILITY = gql`
  query ProjectEligibility($where: ProjectWhereInput, $custom: JSONObject) {
    projectEligibility(where: $where, custom: $custom) {
      status
      eligibleDate
      author {
        id
        firstname
        lastname
      }
    }
  }
`;

export const SEARCH_PROJECT_GRA = gql`
  query SearchProjectGRA(
    $where: ProjectWhereInput
    $data: SearchProjectGRAInput
  ) {
    searchProjectGRA(where: $where, data: $data) {
      lastName
      firstName
      graCode
      status
    }
  }
`;

export const PROJECT_TAKEOVER_METRICS = gql`
  query ProjectTakeoverMetrics(
    $where: ProjectWhereInput
    $gt: ProjectWhereInput
    $lt: ProjectWhereInput
  ) {
    projectTakeoverMetrics(where: $where, gt: $gt, lt: $lt) {
      distributedCount
      takenOverCount
      takeoverRate
      averageTakeoverDelayMs
    }
  }
`;

const projects = {
  PROJECTS,
  PROJECTS_LIST,
  COUNT_PROJECTS,
  PROJECT,
  PROJECT_BROKER_TRACER_ID,
  PROJECT_ELIGIBILITY,
  SEARCH_PROJECT_GRA,
  PROJECT_TAKEOVER_METRICS,
};

export default projects;
